import { useState, useEffect } from 'react';
import api from '../services/api';

interface Game {
  id: number;
  codigo: string;
  nombre: string;
}

interface Difficulty {
  id: number;
  nombre: string;
  orden: number;
}

/**
 * Hook personalizado para gestionar el progreso del juego
 * Obtiene automáticamente el juego_id y nivel_id necesarios
 */
export function useGameProgress(gameCode: string, dificultadOrden?: number) {
  const [juegoId, setJuegoId] = useState<number | null>(null);
  const [nivelId, setNivelId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadGameData = async () => {
      try {
        setLoading(true);

        // Obtener el juego por código
        const gameResponse = await api.get(`/games/${gameCode}`);
        const game: Game = gameResponse.data.data;
        setJuegoId(game.id);

        // Si hay dificultad, obtener el nivel_id
        if (dificultadOrden && game.id) {
          const difficultiesResponse = await api.get(`/games/${gameCode}/difficulties`);
          const difficulties: Difficulty[] = difficultiesResponse.data.data;

          // Buscar la dificultad por orden o por ID (para sesiones)
          let difficulty = difficulties.find(d => d.orden === dificultadOrden);
          if (!difficulty) {
            difficulty = difficulties.find(d => d.id === dificultadOrden);
          }

          if (difficulty) {
            setNivelId(difficulty.id);
          }
        }
      } catch (error) {
        console.error('Error cargando datos del juego:', error);
      } finally {
        setLoading(false);
      }
    };

    loadGameData();
  }, [gameCode, dificultadOrden]);

  /**
   * Guardar progreso del juego
   */
  const saveProgress = async (data: {
    tablero_id?: number;
    tiempo: number;
    movimientos: number;
    tiempo_reaccion?: number;
    completado?: boolean;
  }) => {
    if (!juegoId) {
      console.error('No se puede guardar progreso: juego_id no disponible');
      return;
    }

    try {
      const payload: any = {
        tipo_nivel: gameCode,
        tiempo: data.tiempo,
        movimientos: data.movimientos,
        completado: data.completado !== undefined ? data.completado : true,
      };

      // Solo incluir campos opcionales si tienen valor
      if (juegoId) payload.juego_id = juegoId;
      if (nivelId) payload.nivel_id = nivelId;
      if (data.tablero_id) payload.tablero_id = data.tablero_id;
      if (data.tiempo_reaccion) payload.tiempo_reaccion = data.tiempo_reaccion;

      await api.post('/progress', payload);
    } catch (error) {
      console.error('Error al guardar progreso:', error);
      throw error;
    }
  };

  return {
    juegoId,
    nivelId,
    loading,
    saveProgress,
  };
}
